/** Abalone's serde representation. Index 0 = A1, index 60 = I9. */
export type Side = 'First' | 'Second';
export type AbalonePiece = 0 | 1 | 2;
export type AbaloneDirection = 0 | 1 | 2 | 3 | 4 | 5;

export interface AbaloneOutcome {
  winner?: Side | null;
  kind: 'SixOff' | 'NoMoves' | 'PlyCap';
  first_pushed_off: number;
  second_pushed_off: number;
}

export interface AbaloneState {
  board: AbalonePiece[];
  to_move: Side;
  ply: number;
  first_pushed_off: number;
  second_pushed_off: number;
  layout: 'Standard' | 'BelgianDaisy';
  max_plies: number;
  // serde-wasm-bindgen omits Option::None fields; JSON snapshots can use null.
  outcome?: AbaloneOutcome | null;
}

export interface AbaloneMove {
  cells: number[];
  dir: AbaloneDirection;
}

export const ABALONE_DIRECTIONS = ['E', 'NE', 'NW', 'W', 'SW', 'SE'] as const;
export const ABALONE_DELTAS = [
  [0, 1], [1, 1], [1, 0], [0, -1], [-1, -1], [-1, 0],
] as const;

export const ABALONE_COORDS: ReadonlyArray<readonly [number, number]> = Array.from(
  { length: 9 },
  (_, row) => Array.from(
    { length: row <= 4 ? row + 5 : 13 - row },
    (_, offset) => [row, Math.max(0, row - 4) + offset] as const,
  ),
).flat();

export function abaloneIndex(row: number, col: number): number | null {
  if (row < 0 || row > 8 || col < 0 || col > 8 || Math.abs(col - row) > 4) return null;
  const index = ABALONE_COORDS.findIndex(([r, c]) => r === row && c === col);
  return index < 0 ? null : index;
}

export function abaloneCellName(index: number): string {
  const coord = ABALONE_COORDS[index];
  return coord ? `${String.fromCharCode(65 + coord[0])}${coord[1] + 1}` : '?';
}

export function abaloneNeighbor(index: number, dir: AbaloneDirection): number | null {
  const coord = ABALONE_COORDS[index];
  if (!coord) return null;
  const [dr, dc] = ABALONE_DELTAS[dir];
  return abaloneIndex(coord[0] + dr, coord[1] + dc);
}

/** Cell centers, with A at the bottom; Second sees the board rotated by 180°. */
export function abalonePosition(index: number, flipped = false): { left: number; top: number } {
  const [row, col] = ABALONE_COORDS[index];
  const left = 10 + (col - row / 2 + 2) * 10;
  const top = 90 - row * 10;
  return flipped ? { left: 100 - left, top: 100 - top } : { left, top };
}

export function abaloneSelectionKey(cells: readonly number[]): string {
  return [...cells].sort((a, b) => a - b).join(',');
}

/** Exact engine moves for the selected group; never manufacture a move locally. */
export function abaloneSelectionMoves(
  legal: readonly AbaloneMove[] | undefined,
  cells: readonly number[],
): AbaloneMove[] {
  if (cells.length === 0) return [];
  const key = abaloneSelectionKey(cells);
  return (legal ?? []).filter((move) => abaloneSelectionKey(move.cells) === key);
}

export function isAbaloneStraightGroup(cells: readonly number[]): boolean {
  if (cells.length < 1 || cells.length > 3 || new Set(cells).size !== cells.length) return false;
  if (cells.some((cell) => ABALONE_COORDS[cell] === undefined)) return false;
  return cells.some((start) => ABALONE_DELTAS.some((_, d) => {
    let current: number | null = start;
    for (let i = 0; i < cells.length; i++) {
      if (current === null || !cells.includes(current)) return false;
      current = abaloneNeighbor(current, d as AbaloneDirection);
    }
    return true;
  }));
}

export function describeAbaloneMove(state: AbaloneState, move: AbaloneMove): string {
  const first = ABALONE_COORDS[move.cells[0]];
  const last = ABALONE_COORDS[move.cells[move.cells.length - 1]];
  const [dr, dc] = ABALONE_DELTAS[move.dir];
  const inline = move.cells.length === 1 || (last[0] - first[0]) * dc === (last[1] - first[1]) * dr;
  if (!inline) return `${move.cells.length}玉を横移動`;

  const front = move.cells.find((cell) => {
    const next = abaloneNeighbor(cell, move.dir);
    return next === null || !move.cells.includes(next);
  });
  let next = front === undefined ? null : abaloneNeighbor(front, move.dir);
  const opponent = state.to_move === 'First' ? 2 : 1;
  let pushed = 0;
  while (next !== null && state.board[next] === opponent) {
    pushed++;
    next = abaloneNeighbor(next, move.dir);
  }
  if (pushed > 0) return `相手の${pushed}玉を押す${next === null ? '・1玉を場外へ' : ''}`;
  return `${move.cells.length}玉を移動`;
}

export function abaloneOutcomeText(outcome: AbaloneOutcome): string {
  const result = outcome.winner == null ? '引き分け' : `${outcome.winner === 'First' ? '黒' : '白'}の勝ち`;
  const reason = { SixOff: '6玉を場外へ', NoMoves: '合法手なし', PlyCap: '手数上限' }[outcome.kind];
  return `${result}（${reason}）`;
}
